import { baseApi } from "./baseApi";

export interface BranchRaw {
  id: string;
  branch_detail: {
    status: string;
    branch_name: string;
    branch_address: string;
    location: string;
    branch_email: string;
    branch_phone: string;
  };
  branch_manager: {
    manager_id: string;
    manager_name: string;
  };
  status: string;
  active_orders: string;
  time: string;
}

export interface BranchDetailResponse {
  status: string;
  data: {
    total_branch: number;
    total_active_branch: number;
    total_inactive_branch: number;
    branchInfo: {
      data: BranchRaw[];
      links: Record<string, any>;
      meta: Record<string, any>;
    };
  };
}

// creating or updating a branch
export interface BranchRequest {
  branch_name: string;
  branch_address: string;
  location: string;
  branch_email: string;
  branch_phone: string;
  manager_id: string;
  status: string;
  user_id?: string;
}

// manager type
export interface Manager {
  id: string;
  user: {
    name: string;
    email: string;
  };
}

export interface GetManagersResponse {
  status: string;
  data: {
    message: string;
    staff_members: Manager[];
  };
}

export const branchApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // get all branches
    getAllBranches: builder.query<
      BranchDetailResponse,
      { page?: number; limit?: number; user_id: string }
    >({
      query: ({ page = 1, limit = 10, user_id }) => ({
        url: `/branches/all-branches?page=${page}&limit=${limit}&user_id=${user_id}`,
        method: "GET",
      }),
      providesTags: ["branches"],
    }),

    // get single branch
    getBranchById: builder.query<BranchRaw, string>({
      query: (id) => ({
        url: `/branches/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "branches", id }],
    }),

    // create branch
    createBranch: builder.mutation<BranchRaw, Partial<BranchRequest>>({
      query: (body) => ({
        url: "/branches",
        method: "POST",
        body,
      }),
      invalidatesTags: ["planLimit", "branches"],
    }),

    // update branch
    updateBranch: builder.mutation<
      BranchRaw,
      { id: string; data: Partial<BranchRequest> }
    >({
      query: ({ id, data }) => ({
        url: `/branches/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "planLimit",
        { type: "branches", id },
        "branches",
      ],
    }),

    // delete branch
    deleteBranch: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/branches/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        "planLimit",
        { type: "branches", id },
        "branches",
      ],
    }),
    deactivateBranch: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/branches/branches/${id}/deactivate`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        "planLimit",
        { type: "branches", id },
        "branches",
      ],
    }),

    reactivateBranch: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/branches/branches/${id}/reactivate`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        "planLimit",
        { type: "branches", id },
        "branches",
      ],
    }),

    // get all managers
    getManagers: builder.query<GetManagersResponse, void>({
      query: () => ({
        url: "/employees/staff-members/by-company",
        method: "GET",
      }),
    }),


    // download bulk upload templatefor branch
    downloadBranchTemplate: builder.query<Blob, void>({
      query: () => ({
        url: "/branches/download/branch-template",
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),

    // bulk upload branch (CSV or XLSX)
    bulkUploadBranch: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: `/branches/bulk-upload`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["planLimit", "staff", "invites"],
    }),
  }),
  overrideExisting: false,
});






export const {
  useGetAllBranchesQuery,
  useGetBranchByIdQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useDeactivateBranchMutation,
  useReactivateBranchMutation,
  useGetManagersQuery,
  useLazyDownloadBranchTemplateQuery,
  useBulkUploadBranchMutation,

} = branchApi;
